import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const oneWeek = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < oneWeek) {
        return;
      }
    }

    // Detect iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Show iOS prompt after delay
    if (isIOSDevice) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // Handle beforeinstallprompt for Android/Desktop
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Handle app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-fadeInUp">
      <div className="bg-stone-900 border border-stone-700 rounded-lg p-4 shadow-2xl">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-stone-500 hover:text-white"
          aria-label="Dismiss"
        >
          <X size={20} />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg text-white uppercase">Install IronLink</h3>
            <p className="text-stone-400 text-sm mt-1">
              {isIOS 
                ? 'Tap the share button and "Add to Home Screen" for the best experience.'
                : 'Install our app for quick access and offline support.'
              }
            </p>
          </div>
        </div>

        {!isIOS && deferredPrompt && (
          <Button 
            onClick={handleInstall}
            className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase"
          >
            <Download className="w-4 h-4 mr-2" />
            Install App
          </Button>
        )}

        {isIOS && (
          <div className="mt-4 p-3 bg-stone-800 rounded-md">
            <p className="text-stone-300 text-sm">
              <span className="text-orange-500">1.</span> Tap the <strong>Share</strong> button below
              <br />
              <span className="text-orange-500">2.</span> Scroll and tap <strong>"Add to Home Screen"</strong>
              <br />
              <span className="text-orange-500">3.</span> Tap <strong>"Add"</strong> to install
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
